import React from 'react';
import { Calendar, MapPin, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { eventInfo } from '../data/mock';
import Logo from './Logo';

const Hero = () => {
  const scrollToNext = () => {
    const element = document.querySelector('#om-os');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Velkomst til Vejles Juletræsfest"
      itemScope 
      itemType="https://schema.org/Event"
    >
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a472a] via-[#2d5a3d] to-[#1a472a]" aria-hidden="true" />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {/* Snowflakes */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#c41e3a]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#f5d742]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/20">
            <span className="w-2 h-2 bg-[#f5d742] rounded-full animate-pulse" aria-hidden="true" />
            <span className="text-white/90 text-sm font-medium">
              Afholdes for {eventInfo.year}. gang
            </span>
          </div>

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-full p-6 shadow-2xl">
              <Logo size={140} className="drop-shadow-lg" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 tracking-tight" itemProp="name">
            Vejles
            <span className="block text-[#f5d742]">Juletræsfest</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-white/80 mb-12 max-w-2xl mx-auto" itemProp="description">
            {eventInfo.subtitle}
          </p>

          {/* Event Info Cards */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
              <Calendar className="w-5 h-5 text-[#f5d742]" aria-hidden="true" />
              <time dateTime="2026-12-14" className="text-white font-medium" itemProp="startDate">{eventInfo.date}</time>
            </div>
            <address className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20 not-italic" itemProp="location" itemScope itemType="https://schema.org/Place">
              <MapPin className="w-5 h-5 text-[#f5d742]" aria-hidden="true" />
              <span className="text-white font-medium" itemProp="name">{eventInfo.location}</span>
            </address>
          </div>

          {/* CTA Button */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => document.querySelector('#om-os')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#c41e3a] hover:bg-[#a01830] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Læs Mere
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToNext}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors cursor-pointer"
        aria-label="Scroll ned til Om Os sektionen"
      >
        <ChevronDown className="w-8 h-8 animate-bounce" aria-hidden="true" />
      </button>
    </section>
  );
};

export default Hero;
